import { ReactNode } from 'react'
import { styled, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { IKyc, IKycAddress } from '@/types'
import { capitalizeWords, safeFormatDate } from '@/utils/common'
import { FRANKIE_PORTAL_BASE_URL } from '@/utils/constants'

const firstDocDetails = (row: IKyc) => row.document?.[0]?.documentDetails

const idNumber = (row: IKyc) => {
  const d = firstDocDetails(row)
  return d?.passport?.idNumber ?? d?.driverLicense?.idNumber ?? d?.medicare?.idNumber ?? ''
}
const issuingCountry = (row: IKyc) => {
  const d = firstDocDetails(row)
  return d?.passport?.country ?? d?.driverLicense?.country ?? d?.medicare?.country ?? ''
}
const medicareValidTo = (row: IKyc) => {
  const m = firstDocDetails(row)?.medicare
  if (!m) return ''
  const parts = [m.validToDay, m.validToMonth, m.validToYear]
  return parts.some(Boolean) ? parts.map(p => p ?? '').join('-') : ''
}

const formatEnum = (value?: string) =>
  value && value !== 'UNSPECIFIED' ? capitalizeWords(value.toLowerCase().replace(/_/g, ' ')) : ''

const formatAddress = (a?: IKycAddress) => {
  if (!a) return ''
  return [
    a.unitNumber,
    a.streetNumber,
    a.streetName,
    a.streetType,
    a.suburb,
    a.state,
    a.postCode,
    a.country,
  ]
    .filter(Boolean)
    .join(' ')
}

const dateTime = (value?: string) => (value ? safeFormatDate(value, 'MMM D, YYYY hh:mm A') : '')

const cell = (value?: ReactNode) => <StyledText>{value || ''}</StyledText>

const entityIdCell = (row: IKyc) => {
  const entityId = row.providerEntityId
  // Falls back to plain text until the portal URL is configured.
  if (!entityId || !FRANKIE_PORTAL_BASE_URL) return cell(entityId)

  return cell(
    <ExternalLink
      href={`${FRANKIE_PORTAL_BASE_URL}/${entityId}/general-information`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {entityId}
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </ExternalLink>
  )
}

export const kycColumns: ITableColumn<IKyc>[] = [
  {
    key: 'firstName',
    id: 'firstName',
    label: 'First Name',
    render: (_v, r) => cell(r.personalDetails?.firstName),
  },
  {
    key: 'middleName',
    id: 'middleName',
    label: 'Middle Name',
    render: (_v, r) => cell(r.personalDetails?.middleName),
  },
  {
    key: 'lastName',
    id: 'lastName',
    label: 'Last Name',
    render: (_v, r) => cell(r.personalDetails?.lastName),
  },
  {
    key: 'dob',
    id: 'dob',
    label: 'DOB',
    minWidth: '120px',
    render: (_v, r) => cell(r.personalDetails?.dateOfBirth),
  },
  {
    key: 'status',
    id: 'status',
    label: 'KYC Status',
    minWidth: '130px',
    render: (_v, r) => cell(formatEnum(r.status)),
  },
  {
    key: 'actionRecommended',
    id: 'actionRecommended',
    label: 'Recommended Action',
    render: (_v, r) => cell(r.actionRecommended),
  },
  {
    key: 'providerEntityId',
    id: 'providerEntityId',
    label: 'Entity Id',
    minWidth: '170px',
    render: (_v, r) => entityIdCell(r),
  },
  {
    key: 'providerRequestId',
    id: 'providerRequestId',
    label: 'Request Id',
    minWidth: '170px',
    render: (_v, r) => cell(r.providerRequestId),
  },
  {
    key: 'consentDateTime',
    id: 'consentDateTime',
    label: 'Consent At',
    minWidth: '170px',
    render: (_v, r) => cell(dateTime(r.consentDateTime)),
  },
  {
    key: 'documentType',
    id: 'documentType',
    group: 'Id Documents',
    label: 'Type',
    minWidth: '140px',
    render: (_v, r) => cell(formatEnum(r.document?.[0]?.documentType)),
  },
  {
    key: 'documentScope',
    id: 'documentScope',
    group: 'Id Documents',
    label: 'Sub Type',
    render: (_v, r) => cell(r.document?.[0]?.documentScope),
  },
  {
    key: 'idNumber',
    id: 'idNumber',
    group: 'Id Documents',
    label: 'Number',
    render: (_v, r) => cell(idNumber(r)),
  },
  {
    key: 'issuingCountry',
    id: 'issuingCountry',
    group: 'Id Documents',
    label: 'Issuing Country',
    render: (_v, r) => cell(issuingCountry(r)),
  },
  {
    key: 'issuingState',
    id: 'issuingState',
    group: 'Id Documents',
    label: 'Issuing State',
    render: (_v, r) => cell(formatEnum(firstDocDetails(r)?.driverLicense?.state)),
  },
  {
    key: 'expiryDate',
    id: 'expiryDate',
    group: 'Id Documents',
    label: 'Expiry Date',
    render: (_v, r) => cell(firstDocDetails(r)?.driverLicense?.expiryDate),
  },
  {
    key: 'medicarePosition',
    id: 'medicarePosition',
    label: 'Medicare Position',
    render: (_v, r) => cell(firstDocDetails(r)?.medicare?.positionOnCard),
  },
  {
    key: 'medicareValidTo',
    id: 'medicareValidTo',
    label: 'Medicare Validity',
    render: (_v, r) => cell(medicareValidTo(r)),
  },
  {
    key: 'unitNumber',
    id: 'unitNumber',
    label: 'Unit Number',
    render: (_v, r) => cell(r.address?.unitNumber),
  },
  {
    key: 'streetNumber',
    id: 'streetNumber',
    label: 'Street Number',
    render: (_v, r) => cell(r.address?.streetNumber),
  },
  {
    key: 'streetName',
    id: 'streetName',
    label: 'Street Name',
    render: (_v, r) => cell(r.address?.streetName),
  },
  {
    key: 'streetType',
    id: 'streetType',
    label: 'Street Type',
    render: (_v, r) => cell(r.address?.streetType),
  },
  { key: 'suburb', id: 'suburb', label: 'Suburb', render: (_v, r) => cell(r.address?.suburb) },
  { key: 'state', id: 'state', label: 'State', render: (_v, r) => cell(r.address?.state) },
  { key: 'country', id: 'country', label: 'Country', render: (_v, r) => cell(r.address?.country) },
  {
    key: 'previousAddress',
    id: 'previousAddress',
    label: 'Previous Address',
    minWidth: '160px',
    render: (_v, r) => cell(formatAddress(r.previousAddress)),
  },
  {
    key: 'createdAt',
    id: 'createdAt',
    label: 'Created At',
    minWidth: '170px',
    render: (_v, r) => cell(dateTime(r.createdAt)),
  },
]

const ExternalLink = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
})

const StyledText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  textAlign: 'left',
  color: theme.palette.secondary.main,
}))
